import {
	autoUpdate,
	flip,
	offset,
	shift,
	useFloating,
} from '@floating-ui/react';
import {
	faCheck,
	faChevronDown,
	faSpinner,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Listbox, Transition } from '@headlessui/react';
import React, { Fragment, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

interface SelectOption {
	value: string | number;
	label: string;
	disabled?: boolean;
}

// `size` is omitted along with `onChange` — this component repurposes
// `size` as a design-size token, not the native row count.
interface SelectProps
	extends Omit<
		React.SelectHTMLAttributes<HTMLSelectElement>,
		'onChange' | 'size'
	> {
	options: SelectOption[];
	placeholder?: string;
	error?: boolean;
	fullWidth?: boolean;
	onChange?: (value: string) => void;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	allowClear?: boolean;
	onOpen?: () => void;
	isLoading?: boolean;
	loadingText?: string;
}

export function Select({
	options,
	placeholder,
	error = false,
	fullWidth = true,
	onChange,
	disabled,
	className,
	value,
	size = 'md',
	allowClear = false,
	onOpen,
	isLoading = false,
	loadingText = 'Loading...',
}: SelectProps) {
	const colors = useThemeColors();
	const wasOpenRef = useRef(false);

	// Floating UI to render the menu in a portal at body-level so it
	// escapes any ancestor `overflow-auto` (sidebars, scroll containers)
	// that would otherwise clip the dropdown. Mirrors the SelectSearch
	// pattern used elsewhere in this codebase.
	const { x, y, strategy, refs } = useFloating({
		placement: 'bottom-start',
		middleware: [offset(4), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
	});

	// Find the selected option
	const selectedOption = options?.find((opt) => opt.value === value);

	// Size-based styling to match MultiSelect
	const sizeClasses = {
		xs: 'px-2.5 py-1 text-xs h-[var(--control-h-xs,2rem)]',
		sm: 'px-3 py-1.5 text-sm min-h-[30px]',
		md: 'px-3 py-2 text-sm h-[var(--control-h,2.25rem)]',
		lg: 'px-4 py-2.5 text-base min-h-[42px]',
	}[size];

	// Size-based styling for dropdown options
	const optionSizeClasses = {
		xs: 'py-1 pl-2.5 pr-7 text-xs',
		sm: 'py-1.5 pl-3 pr-7 text-sm',
		md: 'py-1.5 pl-3 pr-7 text-sm',
		lg: 'py-2 pl-4 pr-8 text-base',
	}[size];

	const handleChange = useCallback(
		(optionValue: string | number) => {
			onChange?.(String(optionValue));
		},
		[onChange],
	);

	const handleClear = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			onChange?.('');
		},
		[onChange],
	);

	const hasValue = value != null && value !== '';
	const showClearButton = allowClear && hasValue && !disabled;

	return (
		<Listbox value={value ?? ''} onChange={handleChange} disabled={disabled}>
			{({ open }) => {
				// Call onOpen when dropdown opens
				if (open && !wasOpenRef.current && onOpen != null) {
					// Use setTimeout to avoid calling during render
					setTimeout(() => {
						onOpen();
					}, 0);
				}
				wasOpenRef.current = open;

				return (
					<div className={cn('relative', fullWidth ? 'w-full' : 'w-auto')}>
						<Listbox.Button
							ref={refs.setReference}
							className={cn(
								// Base styling
								'relative w-full cursor-pointer text-left',

								// Adjust right padding based on whether we have clear button
								showClearButton ? 'pr-16' : 'pr-10',

								// Size-based padding and text
								sizeClasses,

								// Use form theme classes
								colors.classes.formFieldBase,

								// State-based styling
								error
									? colors.classes.formFieldError
									: colors.classes.formFieldDefault,

								// Focus state
								colors.classes.formFieldFocus,

								disabled && colors.classes.formFieldDisabled,

								fullWidth && 'w-full',
								className,
							)}
						>
							<span
								className={cn(
									'block truncate',
									!selectedOption &&
										placeholder &&
										colors.classes.textSecondary,
								)}
							>
								{selectedOption
									? selectedOption.label
									: placeholder || 'Select...'}
							</span>

							{/* Clear button — matches BaseTextInput's clear: just a
							    color transition on the icon, no background. */}
							{showClearButton && (
								<div
									role="button"
									tabIndex={0}
									onClick={handleClear}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleClear(e as any);
										}
									}}
									className={cn(
										'absolute inset-y-0 right-8 flex items-center justify-center w-6 cursor-pointer',
									)}
								>
									<FontAwesomeIcon
										icon={faXmark}
										className={cn(
											'h-3 w-3',
											colors.classes.textSecondary,
											'hover:' + colors.classes.textPrimary,
											'transition-colors',
										)}
										aria-hidden="true"
									/>
								</div>
							)}

							{/* Dropdown chevron */}
							<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
								<FontAwesomeIcon
									icon={faChevronDown}
									className={cn('h-3 w-3', colors.classes.textSecondary)}
									aria-hidden="true"
								/>
							</span>
						</Listbox.Button>

						{createPortal(
							<Transition
								as={Fragment}
								show={open}
								leave="transition ease-in duration-100"
								leaveFrom="opacity-100"
								leaveTo="opacity-0"
							>
								<Listbox.Options
									ref={refs.setFloating}
									static
									style={{
										position: strategy,
										top: y ?? 0,
										left: x ?? 0,
										width:
											refs.reference.current?.getBoundingClientRect().width,
										zIndex: 1000,
									}}
									className={cn(
										'max-h-60 overflow-auto custom-scrollbar rounded-md py-1 text-base sm:text-sm',
										colors.classes.selectMenu,
									)}
								>
									{isLoading ? (
										<div
											className={cn(
												'relative cursor-default py-2 px-3 text-center',
												colors.classes.textSecondary,
											)}
										>
											<FontAwesomeIcon
												icon={faSpinner}
												className="animate-spin mr-2"
											/>
											{loadingText}
										</div>
									) : (
										<>
											{placeholder != null && (
												<Listbox.Option
													key="__placeholder__"
													value=""
													className={({ active }) =>
														cn(
															'relative cursor-pointer transition-colors',
															optionSizeClasses,
															active && colors.classes.selectOption,
															!active && colors.classes.textSecondary,
														)
													}
												>
													{placeholder}
												</Listbox.Option>
											)}
											{(options ?? []).map((option) => (
												<Listbox.Option
													key={option.value}
													value={option.value}
													disabled={option.disabled}
													className={({ active, selected }) =>
														cn(
															'relative transition-colors',
															optionSizeClasses,
															option.disabled
																? 'cursor-not-allowed opacity-50'
																: 'cursor-pointer',
															selected &&
																!active &&
																colors.classes.selectOptionSelected,
															active && colors.classes.selectOption,
															!selected &&
																!active &&
																colors.classes.textPrimary,
														)
													}
												>
													{({ selected }) => (
														<>
															<span
																className={cn(
																	'block truncate',
																	selected ? 'font-medium' : 'font-normal',
																)}
															>
																{option.label}
															</span>
															{selected && (
																<span
																	className={cn(
																		'absolute inset-y-0 right-0 flex items-center pr-1.5',
																		colors.classes.textPrimary,
																	)}
																>
																	<FontAwesomeIcon
																		icon={faCheck}
																		className="h-3 w-3"
																		aria-hidden="true"
																	/>
																</span>
															)}
														</>
													)}
												</Listbox.Option>
											))}
										</>
									)}
								</Listbox.Options>
							</Transition>,
							document.body,
						)}
					</div>
				);
			}}
		</Listbox>
	);
}
